/*
    Демо-форма стандартизации.

    Рендерит демо-форму с кнопками переключения типов.
    Предоставляет слоты для хэдера, ссылок под формой, переключателей и для самой формы.
    Слоты используются в версии страницы /clean для авторизованных пользователей:
        - меняется заголовок блока
        - добавляется тогглер типа формы - загрузка из файла или стандартная форма
        - при выбранном типе загрузки из файла - вместо формы выводится загрузчик файлов
        - при выбранном типе загрузки из файла выводятся новые ссылки (включая ссылка на API)

    Для стандартной формы используются предзаполненные значения полей и результирующие данные.
    При отправке формы получает стандартизированные данные из API стандартизации,
    рендерит компонент для вывода результатов
    (для address, name и email используются компоненты из подсказочной формы),
    передавая ему данные.

    При переключении типов обнуляет состояние.
    Выводит "попап-ловушку" при использовании формы по адресам.
    Отправляет событие при успехе (отправке формы) в аналитику
    (реализация в компоненте демо-формы).

    Слушает кастомные события:
        - switch (переключение вкладки в блоке motto страницы) - переключает активную вкладку у себя
        (без прокрутки страницы)

    Для каждой вкладки выводит ссылку на API (вычисляемая на основе активного типа)
    Использует собственные сообщения о качестве введенных данных
    (сообщения из компонентов подсказок не выводятся)
*/

import {
    computed, nextTick,
} from "vue";
import ApiRequest from "../../api-request";
import urls from "../../urls";
import cookies from "../../cookies";
import { inferQcComplete } from "../../domain/address";

import demoForm from "../../components/demo-form/index";
import demoFormMenu from "../../components/demo-form/demo-form-menu";
import addressResults from "../../suggestions/nx-demo/results/address";
import nameResults from "../../suggestions/nx-demo/results/name";
import emailResults from "../../suggestions/nx-demo/results/email";
import phoneResults from "./results/phone";
import passportResults from "./results/passport";
import vehicleResults from "./results/vehicle";

import data from "./data";

const BASE_URL = urls.api.demoClean.getUrl();
const API_TOKEN = window.DA.settings.API_KEY;
const CSRF_TOKEN = cookies.get("csrftoken");

const template = /* html */`
    <demo-form
        :input-label="chosenOption.label || chosenOption.text || ''"
        :loading="loading"
        v-model="value"
        :input-focused="inputFocused"
        :goal-postfix="chosenOptionId.toUpperCase()"
        @form-submit="handleSubmit"
        buttonText="Стандартизировать"
        :error="error"
        data-test="demo"
        :title-tag="titleTag"
    >
        <template #header>
            <slot name="header">
                Готовит данные к бизнес-задачам
            </slot>
        </template>
        <template #togglers>
            <div class="flex-row-sm flex-column gap-col-1_5 gap-row-0_5">
                <slot name="togglers"></slot>
                <demo-form-menu
                    :options="options"
                    v-model="chosenOptionId"
                    v-if="!customForm"
                    @option-click="handleOptionClick"
                />
            </div>
        </template>
        <template #results v-if="showResults && !customForm">
            <div class="flex-column gap-2">
                <div class="flex-column gap-0_5" v-if="messages">
                    <div
                        :class="{
                            'has-success': messages.qcMessage.isCorrect,
                            'has-error': !messages.qcMessage.isCorrect
                        }"
                        class="feedback_wrapper"
                        v-if="messages.qcMessage"
                        data-test="qc"
                    >
                        <span
                            data-ref="feedback"
                            class="feedback"
                            >
                                <i
                                    class="fa fa-lg"
                                    :class="messages.qcMessage.isCorrect
                                        ? 'fa-check-circle'
                                        : 'fa-exclamation-circle'
                                    "
                                ></i> [[ messages.qcMessage.text ]]
                        </span>
                    </div>
                    <div
                        :class="{
                            'has-success': messages.qcCompleteMessage.isCorrect,
                            'has-error': !messages.qcCompleteMessage.isCorrect
                        }"
                        class="feedback_wrapper"
                        v-if="messages.qcCompleteMessage"
                        data-test="qc_complete"
                    >
                        <span
                            data-ref="feedback"
                            class="feedback"
                            >
                                <i
                                    class="fa fa-lg"
                                    :class="messages.qcCompleteMessage.isCorrect
                                        ? 'fa-check-circle'
                                        : 'fa-exclamation-circle'
                                    "
                                ></i> [[ messages.qcCompleteMessage.text ]]
                        </span>
                    </div>
                </div>
                <component
                    :is="resultComponent"
                    :suggestion="results"
                    type="clean"
                    :ready-callback="handleChildReady"
                    v-if="!resultIsEmpty"
                />
            </div>
        </template>
        <template #form v-if="customForm">
            <slot name="form"></slot>
        </template>
        <template #links>
            <div class="flex-row-sm flex-column gap-0_5 gap-sm-1">
                <slot name="links" v-if="customForm"></slot>
                <a v-else :href="apiLink" class="link-white max-w-fit">API-документация</a>
                <template v-if="user.isAuthenticated">
                    <a class="js-payment link-white max-w-fit" href="#pay" data-amount="1000">
                        Пополнить баланс на 1000 ₽
                    </a>
                    <a
                        class="link-white max-w-fit"
                        href="/billing/invoice/?amount=1000"
                        target="_blank"
                    >
                        Счет на оплату
                    </a>
                </template>
            </div>
        </template>
    </demo-form>
`;

export default {
    template,
    props: {
        customForm: {
            type: Boolean,
            default: false,
        },
        titleTag: {
            type: String,
            default: "h2",
        },
    },
    data() {
        return {
            value: "мск сухонска 11/-89",
            options: data.optionsData,
            chosenOptionId: "address",
            inputFocused: false,
            results: {},
            showResults: false,
            hashUsed: false,
            loading: false,
            error: null,
            postDate: null,
            user: window.DA.user,
        };
    },
    watch: {
        // chosenOptionId обновляется при переключении вкладки компонентом demo-form-menu
        // или может быть установлен при обработке хэша
        chosenOptionId() {
            this.clear();
            this.value = this.chosenOption.defaultValue;
        },
        customForm() {
            this.clear();
        },
    },
    computed: {
        chosenOption() {
            const option = this.options.find(o => o.id === this.chosenOptionId);
            return option || {};
        },
        // компонент для вывода результатов по выбранному типу
        resultComponent() {
            return `${this.chosenOptionId}-results`;
        },
        // ссылка на API с учетом активного типа для вывода на странице
        apiLink() {
            return `/api/clean/${this.chosenOptionId}/`;
        },
        // ссылка на API для получения стандартизированных данных с учетом активного типа
        url() {
            return `${BASE_URL}/${this.chosenOptionId}`;
        },
        isDefaultValue() {
            return this.value.trim() === this.chosenOption.defaultValue;
        },
        // сообщения о качестве и пригодности к рассылке (только для адресов)
        // берутся из дефолтных данных или высчитываются по справочникам из data
        messages() {
            if (this.isDefaultValue) {
                if (!this.chosenOption.qcMessage && !this.chosenOption.qcCompleteMessage) {
                    return null;
                }
                return {
                    qcMessage: this.chosenOption.qcMessage,
                    qcCompleteMessage: this.chosenOption.qcCompleteMessage,
                };
            }
            if (!Object.keys(this.results).length) return null;

            const optionQCMessages = data.QC_MESSAGES[this.chosenOptionId];
            const qcMessage = optionQCMessages[this.results.qc] || optionQCMessages.default;

            if (this.chosenOptionId !== "address") {
                return { qcMessage };
            }

            const qcComplete = inferQcComplete(this.results);
            const optionQCCompleteMessages = data.QC_COMPLETE_MESSAGES;
            return {
                qcMessage,
                qcCompleteMessage: optionQCCompleteMessages[qcComplete] ||
                                   optionQCCompleteMessages.default,
            };
        },
        resultIsEmpty() {
            if (["address", "name", "vehicle"].includes(this.chosenOptionId)) {
                return Boolean(!this.results?.result);
            }
            if (["phone", "email"].includes(this.chosenOptionId)) {
                return Boolean(!this.results?.[this.chosenOptionId]);
            }
            return Boolean(this.results?.qc !== 0);
        },
    },
    methods: {
        // фокусирует инпут в форме только при клике на пункт меню
        handleOptionClick() {
            this.inputFocused = false;
            nextTick(() => {
                this.inputFocused = true;
            });
        },

        clear() {
            this.results = {};
            this.showResults = false;
            this.loading = false;
            this.error = null;
        },
        // по готовности компонента с результатами останавливает загрузку
        handleChildReady() {
            this.loading = false;
        },

        handleSubmit() {
            this.clear();
            this.loading = true;

            // если в инпуте дефолтное значение, то делать запрос к API не нужно
            // используются дефолтные данные для вывода результатов
            if (this.isDefaultValue && this.chosenOption.result) {
                this.results = this.chosenOption.result;
                this.showResults = true;
                this.loading = false;
            }

            this.fetchData();
        },

        async fetchData() {
            // запрос данных через API стандартизации
            try {
                const api = new ApiRequest(this.url, {
                    token: API_TOKEN,
                    csrf: CSRF_TOKEN,
                });
                const res = await api.send([this.value]);
                this.results = res[0];
                this.showResults = true;
                if (this.resultIsEmpty) {
                    this.loading = false;
                }
            } catch (e) {
                console.error(e);
                this.clear();
                this.error = "Ошибка связи с сервером";
            }
        },
    },
    mounted() {
        this.postDate = this.$el.parentNode.dataset.post;

        // обработка события переключения вкладок в блоке motto страницы
        document.addEventListener("switch", () => {
            this.chosenOptionId = window.location.hash?.slice(1)
                .split("-")[1];
        });
    },
    // провайдит postDate для компонента с результатами по адресу
    provide() {
        return { postDate: computed(() => this.postDate) };
    },
    components: {
        demoForm,
        demoFormMenu,
        addressResults,
        nameResults,
        emailResults,
        phoneResults,
        passportResults,
        vehicleResults,
    },
};
